// Copyright 2016 Sorama B.V.

import { autoinject, bindable, bindingMode, TaskQueue } from "aurelia-framework";
import { Rect } from "controls/plot/IPlotPart";
import { SpectrogramBasePlot } from "controls/plot/waterfall-plot/SpectrogramBasePlot";
import { ResizeListener } from "libraries/Layouting/ResizeListener";
import { IColorMap } from "services/ColorMap/IColorMap";
import { Generic } from "units/Generic";
import { Unit, UnitValue } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";
import { IPlotParent } from "../IPlotParent";

@bindable({ name: "colormap", defaultBindingMode: bindingMode.oneWay })
@autoinject
export class WaterfallPlot extends SpectrogramBasePlot {
    private static readonly RedrawIntervalTimeMs: number = 30;

    //#region Bindables

    @bindable
    public spectrum: UnitSpectrum;

    @bindable({ changeHandler: "fixedExtremaChanged", defaultBindingMode: bindingMode.oneWay })
    protected fixedMinimum: UnitValue;

    @bindable({ changeHandler: "fixedExtremaChanged", defaultBindingMode: bindingMode.oneWay })
    protected fixedMaximum: UnitValue;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected dataMinimum: UnitValue;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected dataMaximum: UnitValue;

    @bindable
    protected displayUnit: Unit = Generic.unitless;

    @bindable
    protected colormap: IColorMap;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay })
    protected xAxisStart: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay })
    protected xAxisEnd: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay })
    protected yAxisStart: number;

    @bindable({ changeHandler: "axisStartOrEndChanged", defaultBindingMode: bindingMode.twoWay })
    protected yAxisEnd: number;

    @bindable({ changeHandler: "axisBoundStartOrEndChanged", defaultBindingMode: bindingMode.oneWay })
    protected xAxisBoundStart: number;

    @bindable({ changeHandler: "axisBoundStartOrEndChanged", defaultBindingMode: bindingMode.oneWay })
    protected xAxisBoundEnd: number;

    @bindable({ changeHandler: "axisBoundStartOrEndChanged", defaultBindingMode: bindingMode.oneWay })
    protected yAxisBoundStart: number;

    @bindable({ changeHandler: "axisBoundStartOrEndChanged", defaultBindingMode: bindingMode.oneWay })
    protected yAxisBoundEnd: number;

    //#endregion

    //#region Fields

    private redrawTimer: number;
    private extremaUpdateTimer: number;
    private bindTime: number;
    private maxDrawTime: number;

    //#endregion

    constructor(resizeListener: ResizeListener, taskQueue: TaskQueue) {
        super(resizeListener, taskQueue);
    }

    public bind(context: IPlotParent): void {
        super.bind(context);

        this.bindTime = new Date().getTime() / 1000;
        this.axisBoundStartOrEndChanged();

        this.redrawTimer = window.setInterval(() => {
            super.redraw();
        }, WaterfallPlot.RedrawIntervalTimeMs);
    }

    public unbind(): void {
        window.clearInterval(this.extremaUpdateTimer);
        window.clearInterval(this.redrawTimer);
        super.unbind();
    }

    //#region Change handlers

    public axisBoundStartOrEndChanged(): void {
        this.viewPortBounds = new Rect(
            this.xAxisBoundStart,
            this.yAxisBoundStart,
            this.xAxisBoundEnd - this.xAxisBoundStart,
            this.yAxisBoundEnd - this.yAxisBoundStart
        );

        this.xAxisStart = this.xAxisBoundStart;
        this.xAxisEnd = this.xAxisBoundEnd;
        this.yAxisStart = this.yAxisBoundStart;
        this.yAxisEnd = this.yAxisBoundEnd;
    }

    public spectrumChanged(newValue: UnitSpectrum): void {
        if (newValue && newValue.length > 0) {
            const time = (newValue.properties.timeRange.from + newValue.properties.timeRange.to) / 2 - this.bindTime;
            this.maxDrawTime = newValue.properties.timeRange.from = newValue.properties.timeRange.to = time;
            super.addSpectra([newValue]);
        }
    }

    public displayUnitChanged(): void {
        super.clearSpectra();
    }

    //#endregion

    //#region overrides

    protected getDrawTimeOffset(): number {
        const time = new Date().getTime() / 1000 - this.bindTime - 0.1;
        return Math.min(time, this.maxDrawTime);
    }

    //#endregion
}
